import type { CSSProperties, FC, ReactNode } from 'react';
import { Col, Divider, Row } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ellipsisStyling } from '@/utilities';

export const COMPACT_HOVER_CARD_METADATA_ICON_SIZE = '0.75rem';
const COMPACT_HOVER_CARD_METADATA_ROW_HEIGHT = '1.25rem';

export interface CompactHoverCardSurfaceProps {
    children: ReactNode;
    style?: CSSProperties;
    width?: number | string;
}

export const CompactHoverCardSurface: FC<CompactHoverCardSurfaceProps> = ({ children, style, width = '20rem' }) => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                boxShadow: token.boxShadowSecondary,
                boxSizing: 'border-box',
                maxWidth: 'calc(100vw - 2rem)',
                width,
                ...style,
            }}
        >
            {children}
        </div>
    );
};

export interface CompactHoverCardIdentityProps {
    avatar: ReactNode;
    subtitle?: ReactNode;
    title: ReactNode;
    trailing?: ReactNode;
}

export const CompactHoverCardIdentity: FC<CompactHoverCardIdentityProps> = ({ avatar, subtitle, title, trailing }) => {
    const { token } = useAntDesignTheme();

    return (
        <Row align='middle' gutter={12} wrap={false}>
            <Col flex='none'>{avatar}</Col>
            <Col flex='auto' style={{ minWidth: 0, textAlign: 'left' }}>
                <div style={{ ...ellipsisStyling, color: token.colorText, fontWeight: 600 }}>{title}</div>
                {subtitle && (
                    <small style={{ ...ellipsisStyling, color: token.colorTextTertiary, display: 'block' }}>
                        {subtitle}
                    </small>
                )}
            </Col>
            {trailing && <Col flex='none'>{trailing}</Col>}
        </Row>
    );
};

export interface CompactHoverCardSectionProps {
    children: ReactNode;
    divider?: boolean;
    style?: CSSProperties;
}

export const CompactHoverCardSection: FC<CompactHoverCardSectionProps> = ({ children, divider = true, style }) => (
    <>
        {divider && <Divider style={{ margin: 0 }} />}
        <div style={{ padding: '0.625rem 0.75rem', ...style }}>{children}</div>
    </>
);

export interface CompactHoverCardMetadataRowProps {
    children: ReactNode;
    icon: ReactNode;
    trailing?: ReactNode;
}

export const CompactHoverCardMetadataRow: FC<CompactHoverCardMetadataRowProps> = ({ children, icon, trailing }) => {
    const { token } = useAntDesignTheme();

    return (
        <Row
            align='middle'
            gutter={0}
            style={{ columnGap: '0.5rem', minHeight: COMPACT_HOVER_CARD_METADATA_ROW_HEIGHT }}
            wrap={false}
        >
            <Col
                flex='none'
                style={{
                    alignItems: 'center',
                    color: token.colorTextTertiary,
                    display: 'inline-flex',
                    fontSize: COMPACT_HOVER_CARD_METADATA_ICON_SIZE,
                    height: COMPACT_HOVER_CARD_METADATA_ROW_HEIGHT,
                    justifyContent: 'center',
                    lineHeight: 1,
                    width: COMPACT_HOVER_CARD_METADATA_ICON_SIZE,
                }}
            >
                {icon}
            </Col>
            <Col
                flex='auto'
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    minHeight: COMPACT_HOVER_CARD_METADATA_ROW_HEIGHT,
                    minWidth: 0,
                }}
            >
                <small
                    style={{
                        ...ellipsisStyling,
                        alignItems: 'center',
                        color: token.colorTextSecondary,
                        display: 'flex',
                        minHeight: COMPACT_HOVER_CARD_METADATA_ROW_HEIGHT,
                        width: '100%',
                    }}
                >
                    {children}
                </small>
            </Col>
            {trailing && (
                <Col
                    flex='none'
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        height: COMPACT_HOVER_CARD_METADATA_ROW_HEIGHT,
                        justifyContent: 'flex-end',
                    }}
                >
                    {trailing}
                </Col>
            )}
        </Row>
    );
};
